import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isPrintTemplate2Enabled } from "../utils/clinicFeatures";
import baseUrl from "../api/baseUrl";

export type InvoicePrintTemplate = "bill" | "printTemplate_2" | "newBillPrint";

type InvoicePrintTemplateContextValue = {
  template: InvoicePrintTemplate;
  setTemplate: (template: InvoicePrintTemplate) => void;
  options: { value: InvoicePrintTemplate; label: string }[];
  showSelector: boolean;
  clinicDefault: InvoicePrintTemplate | null;
};

const STORAGE_KEY = "invoicePrintTemplate";

const InvoicePrintTemplateContext =
  createContext<InvoicePrintTemplateContextValue | null>(null);

function normalizeTemplate(
  value: string | null | undefined,
  printTemplate2Enabled: boolean
): InvoicePrintTemplate {
  if (value === "newBillPrint") return "newBillPrint";
  if (value === "printTemplate_2" && printTemplate2Enabled) return "printTemplate_2";
  return "bill";
}

export function InvoicePrintTemplateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const printTemplate2Enabled = isPrintTemplate2Enabled();

  const [clinicDefault, setClinicDefault] = useState<InvoicePrintTemplate | null>(null);
  const [template, setTemplateState] = useState<InvoicePrintTemplate>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return normalizeTemplate(saved, printTemplate2Enabled);
  });
  const [clinicSeeded, setClinicSeeded] = useState(false);

  // Clinic Manage Print default for New Bill Print is locked — never override from the header selector.
  const lockedToNewBill = clinicDefault === "newBillPrint";

  const options = useMemo(() => {
    if (lockedToNewBill) {
      return [{ value: "newBillPrint" as const, label: "New Bill Print" }];
    }
    const base: { value: InvoicePrintTemplate; label: string }[] = [
      { value: "bill", label: "Bill" },
    ];
    if (printTemplate2Enabled) {
      base.push({ value: "printTemplate_2", label: "Print Template 2" });
    }
    return base;
  }, [lockedToNewBill, printTemplate2Enabled]);

  useEffect(() => {
    if (lockedToNewBill) {
      setTemplateState("newBillPrint");
      localStorage.setItem(STORAGE_KEY, "newBillPrint");
      return;
    }
    if (!printTemplate2Enabled && template === "printTemplate_2") {
      setTemplateState("bill");
      localStorage.setItem(STORAGE_KEY, "bill");
    }
  }, [lockedToNewBill, printTemplate2Enabled, template]);

  useEffect(() => {
    if (clinicSeeded) return;
    let cancelled = false;

    const applyClinicDefault = (clinicValue: unknown) => {
      const nextDefault = clinicValue
        ? normalizeTemplate(String(clinicValue), printTemplate2Enabled)
        : null;
      setClinicDefault(nextDefault);

      if (nextDefault === "newBillPrint") {
        setTemplateState("newBillPrint");
        localStorage.setItem(STORAGE_KEY, "newBillPrint");
        return;
      }

      if (nextDefault) {
        const local = localStorage.getItem(STORAGE_KEY);
        if (local === "newBillPrint" || !local) {
          setTemplateState(nextDefault);
          localStorage.setItem(STORAGE_KEY, nextDefault);
        } else {
          setTemplateState(normalizeTemplate(local, printTemplate2Enabled));
        }
        return;
      }

      const local = localStorage.getItem(STORAGE_KEY);
      if (local) {
        setTemplateState(normalizeTemplate(local, printTemplate2Enabled));
      }
    };

    baseUrl
      .get("/clinic/getBillData")
      .then((response) => {
        if (cancelled) return;
        applyClinicDefault(response?.data?.data?.invoicePrintDefault);
        setClinicSeeded(true);
      })
      .catch(() => {
        if (!cancelled) setClinicSeeded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicSeeded, printTemplate2Enabled]);

  // When Manage Print saves a new default, lock/unlock without a full reload.
  useEffect(() => {
    const onDefaultChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ invoicePrintDefault?: string }>).detail;
      const next = normalizeTemplate(detail?.invoicePrintDefault, printTemplate2Enabled);
      setClinicDefault(next === "newBillPrint" ? "newBillPrint" : next);
      if (next === "newBillPrint") {
        setTemplateState("newBillPrint");
        localStorage.setItem(STORAGE_KEY, "newBillPrint");
      }
    };
    window.addEventListener("invoicePrintDefaultChanged", onDefaultChanged);
    return () => window.removeEventListener("invoicePrintDefaultChanged", onDefaultChanged);
  }, [printTemplate2Enabled]);

  const setTemplate = useCallback(
    (next: InvoicePrintTemplate) => {
      // Locked when Manage Print default is New Bill Print.
      if (clinicDefault === "newBillPrint") return;
      if (next === "newBillPrint") return;
      setTemplateState(next);
      localStorage.setItem(STORAGE_KEY, next);
    },
    [clinicDefault]
  );

  const value = useMemo(
    () => ({
      template: lockedToNewBill ? "newBillPrint" : template,
      setTemplate,
      options,
      // Hide header switcher when New Bill Print is the clinic default.
      showSelector: !lockedToNewBill && options.length > 1,
      clinicDefault,
    }),
    [lockedToNewBill, template, setTemplate, options, clinicDefault]
  );

  return (
    <InvoicePrintTemplateContext.Provider value={value}>
      {children}
    </InvoicePrintTemplateContext.Provider>
  );
}

export function useInvoicePrintTemplate() {
  const context = useContext(InvoicePrintTemplateContext);
  if (!context) {
    throw new Error(
      "useInvoicePrintTemplate must be used within InvoicePrintTemplateProvider"
    );
  }
  return context;
}
